import { createContext, useContext } from 'react';

export type ResponsiveValue<T> = T | [T, T] | [T, T, T];

/**
 *	A `ui-scale()` step, plus 0 for no gap. Anything past 64 is a CSS override
 *	through `className`.
 */
export type LayoutSpacing =
	| 0
	| 2
	| 4
	| 6
	| 8
	| 10
	| 12
	| 14
	| 16
	| 18
	| 20
	| 22
	| 24
	| 26
	| 28
	| 30
	| 32
	| 34
	| 36
	| 38
	| 40
	| 42
	| 44
	| 46
	| 48
	| 50
	| 52
	| 54
	| 56
	| 58
	| 60
	| 62
	| 64;

export function toSpacingValue(step: LayoutSpacing): string {
	return `${step}px`;
}

/**
 *	Which query governs a subtree's responsive props.
 *
 *	`Row` and `Column` write the same custom properties either way — the mode
 *	only picks which class they carry, and so whether an `@container` or an
 *	`@media` rule reads them.
 */
export type LayoutMode = 'container' | 'viewport';

export interface ContainerConfig {
	mode: LayoutMode;

	/** `container-name`, and so meaningless in `viewport` mode. */
	name?: string;
}

export const ContainerContext = createContext<ContainerConfig | null>(null);

/**
 *	The nearest `Container` or `Viewport`, or `null` outside both.
 *
 *	Nesting resolves by React context, so the nearest provider wins and a
 *	`Container` inside a `Viewport` puts its own subtree back on container
 *	queries.
 */
export function useContainerContext(): ContainerConfig | null {
	return useContext(ContainerContext);
}

export function isResponsiveValue<T>(
	value: ResponsiveValue<T>
): value is [T, T] | [T, T, T] {
	return Array.isArray(value);
}

/**
 *	Normalises a responsive prop into one value per breakpoint.
 *
 *	Both the input tuple and the result are ordered **smallest first** —
 *	`[base, md, lg]` — matching the `min-width` queries in the row and column
 *	stylesheets, under either mode. A 2-tuple leaves the largest step to inherit, so
 *	`[a, b]` reads as "a at base, b from md up".
 *
 *	@param value ResponsiveValue<T>
 *
 *	@returns [T, T, T]
 */
export function toResponsiveValue<T>(value: ResponsiveValue<T>): [T, T, T] {
	if (isResponsiveValue(value)) {
		if (value.length === 3) {
			return value;
		}

		const [defaultValue, responsive] = value;
		return [defaultValue, responsive, responsive];
	}

	return [value, value, value];
}
